import Phaser from 'phaser';
import { FIELD_HEIGHT, FIELD_WIDTH, PRESENTATION_DURATION_MS } from '../../config/gameplay';
import type { PresentationState } from '../../game/simulation/types';
import type { BodyFactory } from '../physics/BodyFactory';
import { ObserverVideoOverlay } from '../../ui/ObserverVideoOverlay';
import { getCharacter } from '../../game/content/characters';
import { DEFAULT_GAME_MODE, phaseName } from '../../game/content/modes';

export class PresentationDirector {
  private active: PresentationState | null = null;
  private timer: Phaser.Time.TimerEvent | null = null;
  private gameOverTimer: Phaser.Time.TimerEvent | null = null;
  private observerVideo: ObserverVideoOverlay | null = null;
  private observerVideoGeneration: number | null = null;
  private readonly visuals: Phaser.GameObjects.GameObject[] = [];

  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly bodies: BodyFactory,
    private readonly onSettle: (id: number, outcome: 'success' | 'failure' | 'cancel') => void,
    private readonly onVideoChange: (video: HTMLVideoElement | null) => void,
  ) {}

  public play(presentation: PresentationState): void {
    this.abort(false);
    this.active = presentation;
    try {
      if (presentation.kind === 'phase') this.playPhase(presentation);
      else if (presentation.kind === 'shinra') this.playShinra(presentation);
      else this.playObserver(presentation);
    } catch {
      this.finish('failure');
    }
  }

  public abort(settle: boolean): void {
    const id = this.active?.id;
    this.cleanup();
    this.active = null;
    if (settle && id !== undefined) this.onSettle(id, 'cancel');
  }

  public playGameOver(isStillGameOver: () => boolean): void {
    this.abort(false);
    this.scene.matter.world.engine.timing.timeScale = 0.22;
    this.gameOverTimer = this.scene.time.delayedCall(PRESENTATION_DURATION_MS.gameOverSlowMotion, () => {
      this.gameOverTimer = null;
      this.scene.matter.world.engine.timing.timeScale = 1;
      if (isStillGameOver()) this.scene.matter.world.pause();
    });
  }

  public destroy(): void {
    this.abort(false);
  }

  private playPhase(presentation: PresentationState): void {
    const veil = this.scene.add.rectangle(FIELD_WIDTH / 2, FIELD_HEIGHT / 2, FIELD_WIDTH, FIELD_HEIGHT, 0x57e5d7, 0);
    veil.setDepth(200);
    const title = this.scene.add
      .text(FIELD_WIDTH / 2, FIELD_HEIGHT * 0.36, `${phaseName(presentation.mode ?? DEFAULT_GAME_MODE, presentation.phase ?? 1)}へ`, {
        fontFamily: 'Kaisei Tokumin, serif',
        fontSize: '30px',
        color: '#f7f1cb',
        stroke: '#151133',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(202)
      .setAlpha(0);
    this.visuals.push(veil, title);
    if (presentation.milestoneCharacterId) {
      const character = getCharacter(presentation.milestoneCharacterId);
      const portrait = this.scene.add.image(FIELD_WIDTH / 2, FIELD_HEIGHT * 0.63, character.textureKey).setDisplaySize(110, 110).setDepth(202);
      const caption = this.scene.add.text(FIELD_WIDTH / 2, FIELD_HEIGHT * 0.82, `${character.name}に到達`, { fontSize: '16px', color: '#f7f1cb', stroke: '#151133', strokeThickness: 3 }).setOrigin(0.5).setDepth(202);
      this.visuals.push(portrait, caption);
    }
    this.scene.tweens.add({ targets: veil, fillAlpha: { from: 0, to: 0.24 }, yoyo: true, duration: 430 });
    this.scene.tweens.add({ targets: title, alpha: 1, scale: { from: 0.82, to: 1 }, yoyo: true, hold: 250, duration: 300 });
    this.schedule(PRESENTATION_DURATION_MS.phase);
  }

  private playShinra(presentation: PresentationState): void {
    const target = this.bodies.get(presentation.entityId);
    const x = target?.body.position.x ?? FIELD_WIDTH / 2;
    const y = target?.body.position.y ?? FIELD_HEIGHT / 2;
    const colors = [0xff6d3a, 0x55d7ff, 0x79e36d, 0xffd84f];
    colors.forEach((color, index) => {
      const angle = (Math.PI * 2 * index) / colors.length - Math.PI / 2;
      const orb = this.scene.add.circle(x + Math.cos(angle) * 170, y + Math.sin(angle) * 170, 13, color, 0.9);
      orb.setDepth(205).setBlendMode(Phaser.BlendModes.ADD);
      this.visuals.push(orb);
      this.scene.tweens.add({ targets: orb, x, y, scale: 0.2, alpha: 0.2, duration: 620, ease: 'Cubic.In' });
    });
    this.scene.cameras.main.shake(280, 0.004);
    this.scene.time.delayedCall(610, () => this.bodies.revealStaged(presentation.entityId));
    this.schedule(PRESENTATION_DURATION_MS.shinra);
  }

  private playObserver(presentation: PresentationState): void {
    this.bodies.revealStaged(presentation.entityId);
    if (this.observerVideoGeneration === presentation.generation) {
      this.finish('success');
      return;
    }
    // Keep this on the scene clock so suspended gameplay does not consume the hold.
    this.timer = this.scene.time.delayedCall(PRESENTATION_DURATION_MS.observer, () => {
      this.timer = null;
      if (this.active !== presentation) return;
      this.observerVideoGeneration = presentation.generation;
      try {
        this.observerVideo = new ObserverVideoOverlay((outcome) => this.finish(outcome), this.onVideoChange);
        this.observerVideo.play();
      } catch {
        this.finish('failure');
      }
    });
  }

  private schedule(duration: number): void {
    this.timer = this.scene.time.delayedCall(duration, () => this.finish('success'));
  }

  private finish(outcome: 'success' | 'failure'): void {
    const active = this.active;
    if (!active) return;
    const id = active.id;
    this.cleanup();
    this.active = null;
    this.onSettle(id, outcome);
  }

  private cleanup(): void {
    this.observerVideo?.destroy();
    this.observerVideo = null;
    this.timer?.remove(false);
    this.timer = null;
    this.gameOverTimer?.remove(false);
    this.gameOverTimer = null;
    this.scene.matter.world.engine.timing.timeScale = 1;
    this.scene.cameras.main.resetFX();
    this.bodies.all().forEach((body) => body.image.clearTint().setAlpha(1));
    for (const visual of this.visuals.splice(0)) visual.destroy();
  }
}
